import { jwtDecode } from "jwt-decode";
import api from "../utils/api";
import { signOutUser } from "../utils/authSession";

// SECURITY NOTE: Storing JWT in localStorage is vulnerable to XSS attacks.
// TODO: Migrate to HttpOnly cookies to prevent token theft via XSS.
// When migrating: backend must set cookie via Set-Cookie header with HttpOnly+Secure+SameSite=Strict

// Persist a fully-resolved session to localStorage.
// Called after normal login and after MFA verification.
export const storeSession = (data, username) => {
  localStorage.setItem("authToken",    data.token);
  localStorage.setItem("refreshToken", data.refreshToken || "");
  localStorage.setItem("username",     username);
  localStorage.setItem("firstName",    data.firstName || "");
  localStorage.setItem("lastName",     data.lastName  || "");
  localStorage.setItem("roles",        JSON.stringify(data.roles || []));
  localStorage.setItem("userId",       data.userId);
  localStorage.setItem("roleId",       data.roleId);
};

// Log in user
export const loginUser = async (username, password) => {
  try {
    const response = await api.post("/authenticate", { username, password });
    const data = response.data;

    // MFA required. session not stored yet; caller shows OTP screen
    if (data.mfaRequired) {
      return {
        mfaRequired: true,
        mfaToken:    data.mfaToken,
        emailHint:   data.emailHint || "",
      };
    }

    storeSession(data, username);
    return data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error || "An error occurred during login."
    );
  }
};

// Check whether token exists and is at least structurally valid
export const checkAuthentication = () => {
  const authToken = localStorage.getItem("authToken");
  const refreshToken = localStorage.getItem("refreshToken");

  if (!authToken && !refreshToken) {
    signOutUser();
    return false;
  }

  if (!authToken && refreshToken) {
    // Allow api.js to try refresh on next request
    return true;
  }

  try {
    const decodedToken = jwtDecode(authToken);

    if (!decodedToken.exp) {
      signOutUser();
      return false;
    }

    const currentTime = Math.floor(Date.now() / 1000);

    // If expired, do NOT force logout here.
    // Let api.js refresh it automatically on the next API call.
    if (decodedToken.exp <= currentTime) {
      return true;
    }

    return true;
  } catch (error) {
    console.error("Invalid token:", error);
    signOutUser();
    return false;
  }
};

// Check role access
export const checkRole = (requiredRoles) => {
  const roles = JSON.parse(localStorage.getItem("roles") || "[]");
  const hasAccess = roles.some((role) => requiredRoles.includes(role));

  if (!hasAccess) {
    window.location.href = "/unauthorized";
    return false;
  }

  return true;
};

// Manual logout
export const signOut = () => {
  signOutUser();
};